import {isEqualWith} from 'lodash';

import {InboxModel} from '../../../../models/api/inboxModel';
import {TagModel} from '../../../../models/api/tagModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {VisualWorkspaceModel} from '../../../../models/api/visualWorkspaceModel';

/*
 * Constants.
 */

export const fourWeeksInMilliseconds = 2419200000;

/*
 * Types.
 */

export type TagModelMap = {[key: TagModel['id']]: TagModel};
export type TeamInboxModelMap = {[key: InboxModel['id']]: InboxModel};
export type TeammateModelMap = {[key: TeammateModel['id']]: TeammateModel};

export interface VisualWorkspaceItemWithTimestamp {
  isRecentlyViewed: undefined | boolean;
  id: number;
}

interface WorkspaceItemWithTimestamp {
  // eslint-disable-next-line camelcase
  last_viewed: undefined | number;
  id: number;
}

/** Map with keys of numeric model IDs and values of timestamps. Used to set isRecentlyViewed */
export interface WorkspaceTimestampMap {
  [key: number]: number | undefined;
}

export interface VisualWorkspaceRecentlyViewedMap {
  [key: number]: boolean | undefined;
}

/*
 * Helpers.
 */

export function createEmptyVisualWorkspace(): VisualWorkspaceModel {
  return {
    teammateNamespace: '',
    nestedFolders: [],
    rootFolders: [],
    teammateWorkspaceTags: [],
    lenses: [],
    teams: [],
    delegations: [],
    teammates: [],
    isAlphabetized: false,
  };
}

export function isSameVisualWorkspace(a: VisualWorkspaceModel, b: VisualWorkspaceModel) {
  return isEqualWith(a, b, (_a, _b, key) => (key === 'isRecentlyViewed' ? true : undefined));
}

// Creates a map of the workspace item ID to its timestamp.
export function computeWorkspaceTimestampMap(workspaceItems: ReadonlyArray<WorkspaceItemWithTimestamp>) {
  return workspaceItems.reduce<WorkspaceTimestampMap>((result, workspaceItem) => {
    // Build the object using mutation, since it may be very big for customers like Better.
    // eslint-disable-next-line no-param-reassign
    result[workspaceItem.id] = workspaceItem.last_viewed;
    return result;
  }, {});
}

export function computeVisualWorkspaceRecentlyViewedMap(
  workspaceItems: ReadonlyArray<VisualWorkspaceItemWithTimestamp>,
) {
  return workspaceItems.reduce<VisualWorkspaceRecentlyViewedMap>((result, workspaceItem) => {
    // Build the object using mutation, since it may be very big for customers like Better.
    // eslint-disable-next-line no-param-reassign
    result[workspaceItem.id] = workspaceItem.isRecentlyViewed;
    return result;
  }, {});
}

/**
 * All items in VisualWorkspaceModel (except for nested folders and delegated items) are forced to have an isRecentlyViewed property.
 * @see VisualWorkspaceModel
 * Thus we know that preferences and workspaces are out of sync when an item has an undefined isRecentlyViewed property.
 */
function computeHasUndefinedIsRecentlyViewed(
  visualWorkspaceItems: ReadonlyArray<VisualWorkspaceItemWithTimestamp>,
): boolean {
  return visualWorkspaceItems.some(({isRecentlyViewed}) => isRecentlyViewed === undefined);
}

export function visualWorkspaceHasUndefinedIsRecentlyViewed(visualWorkspace: VisualWorkspaceModel) {
  const hasUndefinedTagTimestamp = computeHasUndefinedIsRecentlyViewed(visualWorkspace.teammateWorkspaceTags);
  const hasUndefinedTeammateTimestamp = computeHasUndefinedIsRecentlyViewed(visualWorkspace.teammates);
  const hasUndefinedTeamDataTimestamp = visualWorkspace.teams.some(({inboxes, customViews}) => {
    const hasUndefinedInboxTimestamp = computeHasUndefinedIsRecentlyViewed(inboxes);
    const hasUndefinedCustomViewTimestamp = computeHasUndefinedIsRecentlyViewed(customViews);
    return hasUndefinedInboxTimestamp || hasUndefinedCustomViewTimestamp;
  });

  return hasUndefinedTagTimestamp || hasUndefinedTeamDataTimestamp || hasUndefinedTeammateTimestamp;
}

// When the FF is off, we return true if the workspace last_viewed timestamp is defined; we return undefined if
// the workspace last_viewed timestamp is also undefined, because this will trigger a necessary workspace PUT.
// When the FF is on, we return the normal isRecentlyViewed value that can be undefined, true, or false.
export function computeVisualWorkspaceItemIsRecentlyViewed(
  id: number,
  timestampMap: WorkspaceTimestampMap,
  hasP1AutoRec: boolean,
) {
  return computeWorkspaceItemIsRecentlyViewed(timestampMap[id], hasP1AutoRec);
}

// Items are defined as recently viewed if their workspace last_viewed timestamp is within the past four weeks.
// If the item has an undefined last_viewed timestamp, isRecentlyViewed is also undefined.
export function computeWorkspaceItemIsRecentlyViewed(
  lastViewed: number | undefined,
  hasP1AutoRec: boolean,
): boolean | undefined {
  if (!lastViewed) {
    return undefined;
  }

  if (!hasP1AutoRec) {
    return true;
  }

  return Date.now() - lastViewed <= fourWeeksInMilliseconds;
}

export function isOverFourWeeksAgo(timestamp: number | undefined) {
  return timestamp ? Date.now() - timestamp > fourWeeksInMilliseconds : false;
}
